/**
 * Login History (masters only).
 *
 * Shows the last three months of sessions: who signed in, when, how long they
 * were active, from which device and roughly where. The point of the device
 * column is the shared-account question — one account seen on several machines
 * is flagged in the "By user" view.
 *
 * Everything here is defensive on purpose. The page must render even if the
 * backend is one deploy behind and still returns the old
 * `{ username, login_date }` rows: unknown fields simply show a dash.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import {
  FaDesktop,
  FaMobileAlt,
  FaTabletAlt,
  FaTerminal,
  FaQuestion,
  FaSearch,
  FaSyncAlt,
  FaUserFriends,
} from "react-icons/fa";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "all", label: "Last 3 months" },
];

// "Basic" is the label; the database value stays 'SME'.
const TYPE_LABELS = { admin: "Master", CAM: "CAM", org_admin: "Org admin", supplier: "Supplier", SME: "Basic" };
const TYPE_COLORS = { admin: "purple", CAM: "teal", org_admin: "blue", supplier: "gray", SME: "orange" };

const DEVICE_ICONS = { Desktop: FaDesktop, Mobile: FaMobileAlt, Tablet: FaTabletAlt, Script: FaTerminal };

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Monday-based week, which is how the business reads a week.
const startOfWeek = (d) => {
  const s = startOfDay(d);
  const dow = (s.getDay() + 6) % 7;
  s.setDate(s.getDate() - dow);
  return s;
};

const rangeBounds = (key) => {
  const now = new Date();
  switch (key) {
    case "today":
      return [startOfDay(now), null];
    case "week":
      return [startOfWeek(now), null];
    case "month":
      return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    case "lastMonth":
      return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    default:
      return [null, null];
  }
};

const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (d) =>
  d
    ? d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })
    : "—";

const fmtTime = (d) => (d ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }) : "—");

const fmtDuration = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "—";
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "< 1 min";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const ACTIVE_WINDOW_MS = 6 * 60 * 1000; // last seen this recently = still on the page

// "Chrome 151" and "Chrome 152" are the same browser six weeks apart. Dropping
// the version turns a useless count into a usable one: on the backfilled data
// GATEWAY looked like six devices and is really Chrome + Edge + a Samsung
// phone. Still only a hint — the hard count is the device id.
const browserFamily = (label) => (label ? label.replace(/\s\d[\d.]*/g, "") : null);

/**
 * Normalises whatever the API returned into the shape this page draws, so a
 * missing field is never a crash.
 */
const normalise = (raw) => {
  const started = parseDate(raw.started_at || raw.login_date);
  const lastSeen = parseDate(raw.last_seen_at) || started;
  const ended = parseDate(raw.ended_at);
  const legacy = raw.source === "legacy" || !raw.started_at;
  const durationMs = legacy || !started || !lastSeen ? null : Math.max(0, lastSeen - started);
  const deviceLabel = [raw.browser, raw.os].filter(Boolean).join(" · ") || null;
  const place = [raw.city, raw.country].filter(Boolean).join(", ") || null;

  return {
    id: raw.id ?? `${raw.username}-${raw.login_date}`,
    userid: raw.userid ?? null,
    username: raw.username || "—",
    fname: raw.fname || null,
    userType: raw.user_type || null,
    started,
    lastSeen,
    ended,
    endReason: raw.end_reason || null,
    durationMs,
    legacy,
    method: raw.method || null,
    remembered: raw.remembered,
    deviceId: raw.device_id || null,
    deviceLabel,
    deviceType: raw.device_type || null,
    ip: raw.ip || null,
    place,
    isp: raw.isp || null,
    // What counts as "a different machine" is ONLY the browser's own id. Not
    // the browser+OS label and never the raw User-Agent: Chrome rewrites its
    // version every few weeks, so one laptop would show up as four or five
    // "devices" (prod backfill proves it — GATEWAY looked like six machines and
    // is one). Sessions with no device id are counted as unknown, not as a
    // device, so the shared-account flag never cries wolf.
    deviceKey: raw.device_id || null,
  };
};

const DeviceCell = ({ row }) => {
  const IconCmp = DEVICE_ICONS[row.deviceType] || FaQuestion;
  if (!row.deviceLabel && !row.deviceId) return <Text color="gray.400">—</Text>;
  return (
    <HStack spacing={2} align="center">
      <Icon as={IconCmp} color="gray.500" boxSize={3.5} />
      <Box>
        <Text fontSize="sm">{row.deviceLabel || "Unknown browser"}</Text>
        {row.deviceId && (
          <Tooltip label={`Device id ${row.deviceId}`} placement="top">
            <Text fontSize="xs" color="gray.500" fontFamily="mono">
              {row.deviceId.replace(/^d_/, "").slice(0, 6)}
            </Text>
          </Tooltip>
        )}
      </Box>
    </HStack>
  );
};

const TypeBadge = ({ type }) =>
  type ? (
    <Badge colorScheme={TYPE_COLORS[type] || "gray"} variant="subtle" fontSize="0.7em">
      {TYPE_LABELS[type] || type}
    </Badge>
  ) : (
    <Text color="gray.400">—</Text>
  );

const LoginHistory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("week");
  const [userType, setUserType] = useState("");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("sessions");
  const toast = useToast();

  // Org admins (2026-09-17) read the org-scoped endpoint: only the accounts in
  // their own organisation come back. Masters keep the full list.
  const isOrgAdmin = localStorage.getItem("user_type") === "org_admin";
  const historyUrl = `${process.env.REACT_APP_API_URL}/${isOrgAdmin ? "org-admin" : "admin"}/get-login-history`;

  const fetchHistory = useCallback(
    async ({ quiet } = {}) => {
      if (!quiet) setLoading(true);
      try {
        const response = await axios.post(historyUrl, {});
        const list = Array.isArray(response.data?.history) ? response.data.history : [];
        setRows(list.map(normalise));
      } catch (error) {
        toast({
          title: "Could not load login history",
          description: "Please try again in a moment.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [toast, historyUrl]
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const filtered = useMemo(() => {
    const [from, to] = rangeBounds(range);
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (from && (!r.started || r.started < from)) return false;
      if (to && (!r.started || r.started >= to)) return false;
      if (userType && r.userType !== userType) return false;
      if (term) {
        const hay = `${r.username} ${r.fname || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, range, userType, search]);

  const byUser = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = r.userid ?? r.username;
      if (!map.has(key)) {
        map.set(key, {
          key,
          username: r.username,
          fname: r.fname,
          userType: r.userType,
          sessions: 0,
          devices: new Map(),
          browsers: new Set(),
          activeMs: 0,
          lastSeen: null,
        });
      }
      const u = map.get(key);
      u.sessions += 1;
      if (r.durationMs) u.activeMs += r.durationMs;
      if (r.lastSeen && (!u.lastSeen || r.lastSeen > u.lastSeen)) u.lastSeen = r.lastSeen;
      if (r.deviceKey) {
        u.devices.set(r.deviceKey, r.deviceLabel || "Unknown browser");
      } else if (r.deviceLabel) {
        u.browsers.add(browserFamily(r.deviceLabel));
      }
    });
    return Array.from(map.values()).sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0));
  }, [filtered]);

  const stats = useMemo(() => {
    const shared = byUser.filter((u) => u.devices.size > 1);
    return {
      sessions: filtered.length,
      people: byUser.length,
      shared: shared.length,
      sharedNames: shared.map((u) => u.fname || u.username).join(", "),
    };
  }, [filtered, byUser]);

  const types = useMemo(() => Array.from(new Set(rows.map((r) => r.userType).filter(Boolean))).sort(), [rows]);

  const now = Date.now();

  return (
    <Box p={{ base: 3, md: 6 }} maxW="1400px" mx="auto">
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3} mb={1}>
        <Box>
          <Heading size="lg">Login History</Heading>
          <Text color="gray.600" fontSize="sm" mt={1}>
            {isOrgAdmin
              ? "Who in your organisation signed in, when, how long they were active and from which device. Last 3 months."
              : "Who signed in, when, how long they were active and from which device. Last 3 months. Master accounts are not recorded."}
          </Text>
        </Box>
        <Button
          leftIcon={<FaSyncAlt />}
          size="sm"
          variant="outline"
          onClick={() => fetchHistory({ quiet: true })}
          isLoading={loading}
        >
          Refresh
        </Button>
      </Flex>

      {/* Summary */}
      <HStack spacing={6} my={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight="bold" lineHeight="1.1">
            {stats.sessions}
          </Text>
          <Text fontSize="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide">
            Sessions
          </Text>
        </Box>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" lineHeight="1.1">
            {stats.people}
          </Text>
          <Text fontSize="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide">
            Accounts
          </Text>
        </Box>
        <Tooltip
          label={
            stats.sharedNames ||
            "No account has signed in from two different machines since device tracking started. Older sessions carry no device id, so they are not counted here."
          }
        >
          <Box>
            <Text fontSize="2xl" fontWeight="bold" lineHeight="1.1" color={stats.shared ? "orange.600" : undefined}>
              {stats.shared}
            </Text>
            <Text fontSize="xs" color="gray.600" textTransform="uppercase" letterSpacing="wide">
              On 2+ devices
            </Text>
          </Box>
        </Tooltip>
      </HStack>

      {/* Controls */}
      <Flex gap={3} wrap="wrap" align="center" mb={4}>
        <ButtonGroup size="sm" isAttached variant="outline">
          {RANGES.map((r) => (
            <Button
              key={r.key}
              onClick={() => setRange(r.key)}
              colorScheme={range === r.key ? "teal" : "gray"}
              variant={range === r.key ? "solid" : "outline"}
            >
              {r.label}
            </Button>
          ))}
        </ButtonGroup>

        <Select size="sm" w="170px" value={userType} onChange={(e) => setUserType(e.target.value)}>
          <option value="">All user types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t] || t}
            </option>
          ))}
        </Select>

        <InputGroup size="sm" w="220px">
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color="gray.400" boxSize={3} />
          </InputLeftElement>
          <Input placeholder="Search user" value={search} onChange={(e) => setSearch(e.target.value)} />
        </InputGroup>

        <ButtonGroup size="sm" isAttached variant="outline" ml="auto">
          <Button
            onClick={() => setView("sessions")}
            colorScheme={view === "sessions" ? "teal" : "gray"}
            variant={view === "sessions" ? "solid" : "outline"}
          >
            Sessions
          </Button>
          <Button
            leftIcon={<FaUserFriends />}
            onClick={() => setView("users")}
            colorScheme={view === "users" ? "teal" : "gray"}
            variant={view === "users" ? "solid" : "outline"}
          >
            By user
          </Button>
        </ButtonGroup>
      </Flex>

      {loading ? (
        <Flex justify="center" py={16}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : filtered.length === 0 ? (
        <Box py={16} textAlign="center" color="gray.500">
          <Text>No sign-ins in this period.</Text>
        </Box>
      ) : view === "sessions" ? (
        <Box overflowX="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th>User</Th>
                <Th>Type</Th>
                <Th>Signed in</Th>
                <Th>Active for</Th>
                <Th>Last seen</Th>
                <Th>Device</Th>
                <Th>Location</Th>
                <Th>How</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map((r) => {
                const live = r.lastSeen && !r.ended && now - r.lastSeen < ACTIVE_WINDOW_MS;
                return (
                  <Tr key={r.id} _hover={{ bg: "gray.50" }}>
                    <Td>
                      <Text fontWeight="600" fontSize="sm">
                        {r.fname || r.username}
                      </Text>
                      {r.fname && (
                        <Text fontSize="xs" color="gray.500">
                          {r.username}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <TypeBadge type={r.userType} />
                    </Td>
                    <Td whiteSpace="nowrap" fontSize="sm">
                      {fmtDateTime(r.started)}
                    </Td>
                    <Td whiteSpace="nowrap" fontSize="sm">
                      {live ? (
                        <Badge colorScheme="green" variant="subtle">
                          active now
                        </Badge>
                      ) : (
                        <Tooltip
                          label={
                            r.legacy
                              ? "Recorded before session tracking — only the sign-in time is known"
                              : "Time between the first and last action in this session"
                          }
                        >
                          <Text>{fmtDuration(r.durationMs)}</Text>
                        </Tooltip>
                      )}
                    </Td>
                    <Td whiteSpace="nowrap" fontSize="sm" color="gray.600">
                      {r.legacy ? "—" : fmtTime(r.lastSeen)}
                      {r.endReason && (
                        <Text as="span" fontSize="xs" color="gray.400" ml={1}>
                          ({r.endReason})
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <DeviceCell row={r} />
                    </Td>
                    <Td fontSize="sm">
                      {r.place || r.ip ? (
                        <Tooltip label={[r.ip, r.isp].filter(Boolean).join(" · ") || ""} placement="top">
                          <Text>{r.place || r.ip}</Text>
                        </Tooltip>
                      ) : (
                        <Text color="gray.400">—</Text>
                      )}
                    </Td>
                    <Td fontSize="xs" color="gray.600" whiteSpace="nowrap">
                      {r.method || "—"}
                      {r.remembered ? " · remembered" : ""}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>User</Th>
                <Th>Type</Th>
                <Th isNumeric>Sessions</Th>
                <Th>Devices</Th>
                <Th>Total active</Th>
                <Th>Last seen</Th>
              </Tr>
            </Thead>
            <Tbody>
              {byUser.map((u) => (
                <Tr key={u.key} _hover={{ bg: "gray.50" }}>
                  <Td>
                    <Text fontWeight="600" fontSize="sm">
                      {u.fname || u.username}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {u.username}
                    </Text>
                  </Td>
                  <Td>
                    <TypeBadge type={u.userType} />
                  </Td>
                  <Td isNumeric fontSize="sm">
                    {u.sessions}
                  </Td>
                  <Td>
                    <Tooltip
                      label={
                        u.devices.size
                          ? Array.from(u.devices.values()).join("  |  ")
                          : u.browsers.size
                          ? `Older sessions with no device id — a browser is not proof of a separate machine. Seen: ${Array.from(
                              u.browsers
                            ).join(", ")}`
                          : "No device recorded"
                      }
                    >
                      <Badge
                        colorScheme={u.devices.size > 1 ? "orange" : "gray"}
                        variant={u.devices.size > 1 ? "solid" : "subtle"}
                      >
                        {u.devices.size
                          ? `${u.devices.size} device${u.devices.size > 1 ? "s" : ""}`
                          : u.browsers.size
                          ? `~${u.browsers.size} browser${u.browsers.size > 1 ? "s" : ""}`
                          : "—"}
                      </Badge>
                    </Tooltip>
                  </Td>
                  <Td fontSize="sm">{u.activeMs ? fmtDuration(u.activeMs) : "—"}</Td>
                  <Td fontSize="sm" color="gray.600" whiteSpace="nowrap">
                    {fmtDateTime(u.lastSeen)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Text fontSize="xs" color="gray.500" mt={3}>
        A session is a visit: it starts at sign-in and ends after 30 minutes of no activity. "Active for" is the time
        between the first and last action, so reading a screen without clicking does not count. Rows marked with a dash
        pre-date session tracking. The device count only counts browsers that have signed in since this went live — a
        browser version is not a device, so nothing older is guessed at.
      </Text>
    </Box>
  );
};

export default LoginHistory;
